import type { BaseDocument } from '../mongo/base-document.interface';
import type { Location } from '../geolocation';
import type { Contact } from '../contact';
import type { PhoneNumber } from '../phone';

export const CUSTOMER_TYPE = {
  CLIENT: 'client',
  COMPANY: 'company',
} as const;

export type CustomerType = (typeof CUSTOMER_TYPE)[keyof typeof CUSTOMER_TYPE];

export const CUSTOMER_STATUS = {
  VALIDATED: 'validated',
} as const;

export type CustomerStatus = (typeof CUSTOMER_STATUS)[keyof typeof CUSTOMER_STATUS];

export interface CustomerAddressLocation extends Location {
  isDefault?: boolean;
}

export interface CustomerAddress {
  contact?: Contact;
  location: CustomerAddressLocation;
  originalAddress: string;
}

export interface Customer extends BaseDocument {
  addresses: CustomerAddress[];
  companyName?: string;
  email?: string;
  firstName: string;
  lastName: string;
  loyaltyNumber?: string;
  phone: PhoneNumber;
  siteIds: string[];
  spaceIds: string[];
  status: CustomerStatus;
  type: CustomerType;
}

/**
 * Minimal Customer reference embedded (denormalized) into other documents.
 */
export type CustomerSummary = Pick<Customer, 'id' | 'email' | 'firstName' | 'lastName' | 'phone'>;

/**
 * Customer contact details supplied when creating an order (no persisted id yet).
 */
export type CustomerContact = Omit<CustomerSummary, 'id'>;

export type CreateCustomer = Omit<
  Customer,
  'createdAt' | 'deletedAt' | 'id' | 'spaceIds' | 'status' | 'tenantId' | 'updatedAt'
> & {
  /**
   * Optional space-level creation-scope hint, honoured only for space-level creation
   * (when `siteIds` is empty).
   *
   * When `siteIds` is provided, `spaceIds` is derived **backend-authoritatively** from
   * the sites on the backend and this hint is **ignored** — the frontend value is never
   * trusted for site-scoped creates.
   *
   * `spaceIds` is always server-derived; `spaceId` is never persisted.
   */
  spaceId?: string;
};

export interface CustomerResolvedSite {
  id: string;
  name: string;
}

export interface CustomerWithResolvedRefs extends Customer {
  lastOrderDate?: string;
  resolvedSites: CustomerResolvedSite[];
}
